import { COMMISSION_PLAN_LABEL } from '@shared/fees'
import { BROKER_LABEL, BROKER_NOTE } from '@shared/brokers'
import { useEffect, useState } from 'react'
import { ArrowUpRight, Check, ChevronRight, MessageSquare, Pencil, Play, Power, RotateCw, SlidersHorizontal, TriangleAlert, X } from 'lucide-react'
import { stopPath, type PlanLot, type WorkerDetail, type WorkerStatus } from '@shared/worker'
import { workersApi } from '../../lib/api'
import { useWorker } from '../../hooks/useWorker'
import { useStore } from '../../store'
import { fmtPrice } from '../../lib/format'
import { cn } from '../../lib/utils'
import { QuickEdit } from './QuickEdit'

/**
 * A trade worker as a card. Every number on it comes from the server's compiled plan and
 * computed summary — the AI never writes any of it. Drafts carry the approval controls.
 */

export const STATUS_LABEL: Record<WorkerStatus, string> = {
  draft: 'Awaiting approval',
  rejected: 'Rejected',
  expired: 'Expired',
  waiting: 'Armed — waiting to start',
  working: 'Orders working',
  open: 'In position',
  closing: 'Closing',
  done: 'Done',
  cancelled: 'Cancelled',
  error: 'Needs attention',
  applied: 'Change applied',
}

export function statusTone(s: WorkerStatus): 'live' | 'wait' | 'bad' | 'off' {
  if (s === 'open' || s === 'working' || s === 'closing') return 'live'
  if (s === 'draft' || s === 'waiting') return 'wait'
  if (s === 'error') return 'bad'
  return 'off'
}

export const usd = (n: number | null | undefined, signed = false) => {
  if (n == null || Number.isNaN(n)) return '—'
  const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${n < 0 ? '−' : signed && n > 0 ? '+' : ''}$${s}`
}

const btn = 'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-colors disabled:opacity-40'

export function StatusPill({ status }: { status: WorkerStatus }) {
  const tone = statusTone(status)
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-medium',
        tone === 'bad' ? 'text-down' : tone === 'off' ? 'text-fg-faint' : 'text-fg-muted',
        'bg-[var(--color-inset)]',
      )}
    >
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          tone === 'live' ? 'bg-up' : tone === 'wait' ? 'animate-pulse bg-fg-muted' : tone === 'bad' ? 'bg-down' : 'bg-flat',
        )}
      />
      {STATUS_LABEL[status]}
    </span>
  )
}

export function Metric({ label, value, tone, sub }: { label: string; value: string; tone?: 'up' | 'down'; sub?: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-[var(--line-soft)] px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-fg-faint">{label}</div>
      <div className={cn('num mt-0.5 truncate text-[14px] font-semibold', tone === 'up' ? 'text-up' : tone === 'down' ? 'text-down' : 'text-fg')}>
        {value}
      </div>
      {sub && <div className="num truncate text-[10.5px] text-fg-faint" title={sub}>{sub}</div>}
    </div>
  )
}

export function Section({ title, children, right }: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="border-t border-[var(--separator)] px-3.5 py-2.5">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-fg-faint">{title}</span>
        {right}
      </div>
      {children}
    </div>
  )
}

/** Where this lot's stop is planned to go next (rules, insurance), e.g. "→ 102.00 after TP1 → 103.00 after TP2". */
export function stopStepsText(w: WorkerDetail, lot: PlanLot): string {
  const fired = new Set(w.runtime?.firedRules ?? [])
  const live = w.status !== 'draft' ? w.runtime?.lots.find((x) => x.lot === lot.lot)?.stop : undefined
  const reached = (to: number | 'breakeven' | { text: string }) => live != null && typeof to === 'number' && (w.side === 'long' ? live >= to : live <= to)
  return stopPath(w.spec, lot, w.plan)
    .filter((s) => !fired.has(s.rule) && !reached(s.to))
    .map((s) => `→ ${s.to === 'breakeven' ? 'break-even' : typeof s.to === 'number' ? fmtPrice(s.to) : s.to.text} ${s.after}`)
    .join('  ')
}

function StopSteps({ w, lot }: { w: WorkerDetail; lot: PlanLot }) {
  const text = stopStepsText(w, lot)
  return text ? <span className="block text-[11px] text-fg-subtle">{text}</span> : null
}

/** The worker's insurance: the level, each lot's trigger → break-even, and "protect others". */
function Insurance({ w }: { w: WorkerDetail }) {
  const ins = w.plan.insurance
  if (!ins) return null
  const long = w.side === 'long'
  const lots = w.plan.lots.filter((l) => l.insurance)
  const stall = ins.stall?.on ? ins.stall : null
  const kinds = [ins.on ? `break-even ${ins.level}%${ins.basis === 'first' ? ' (TP1)' : ''}` : null, stall ? 'stall → zone' : null].filter(Boolean)
  const title = kinds.length ? `Insurance · ${kinds.join(' + ')}` : 'Insurance · none — the stop stays where it is'
  const inEffect = (l: PlanLot) => {
    const cur = w.status !== 'draft' ? w.runtime?.lots.find((x) => x.lot === l.lot)?.stop : undefined
    return cur != null && l.insurance != null && (long ? cur >= l.insurance.be : cur <= l.insurance.be)
  }
  return (
    <Section title={title}>
      {ins.on && (
        <ul className="space-y-0.5 text-[12px] text-fg-muted">
          {lots.map((l) => (
            <li key={l.lot} className="flex items-start gap-1.5">
              <span className={cn('mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full', inEffect(l) ? 'bg-up' : 'bg-[var(--line-soft)]')} />
              <span className="num">
                Lot {l.lot}:{' '}
                {l.insurance!.trigger != null ? (
                  <>
                    at {fmtPrice(l.insurance!.trigger)} → stop {fmtPrice(l.insurance!.be)}
                  </>
                ) : (
                  <span className="text-fg-faint">not insured — {l.insurance!.skip}</span>
                )}
                {inEffect(l) && <span className="ml-1 text-[10.5px] text-fg-faint">in effect</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {stall && (
        <ul className="mt-1 space-y-0.5 text-[12px] text-fg-muted">
          {lots
            .filter((l) => l.tp)
            .map((l) => (
              <li key={`st${l.lot}`} className="num">
                Lot {l.lot}:{' '}
                {l.insurance!.stallZone != null ? (
                  <>
                    zone {fmtPrice(l.insurance!.stallZone)} · waits {l.insurance!.stallWaitMin} min, then the target drops there
                  </>
                ) : (
                  <span className="text-fg-faint">no stall zone — {l.insurance!.stallSkip}</span>
                )}
                {w.runtime?.stall?.[String(l.lot)]?.done && <span className="ml-1 text-[10.5px] text-fg-faint">lowered</span>}
                {w.runtime?.stall?.[String(l.lot)] && !w.runtime.stall[String(l.lot)].done && <span className="ml-1 text-[10.5px] text-fg-faint">waiting</span>}
              </li>
            ))}
        </ul>
      )}
      {ins.protectOthers && w.plan.lots.length > 1 && (
        <div className="mt-1 text-[11.5px] text-fg-subtle">When a lot takes its profit, the other lots’ stops move to their fee-covered break-even.</div>
      )}
      <div className="mt-1 text-[11px] text-fg-faint">
        {ins.on ? (w.spec.broker === 'robinhood' ? 'Break-even includes Robinhood’s sale fees. ' : 'Break-even includes both IBKR commissions. ') : ''}
        {stall ? 'The stall wait counts trading minutes only (the stock’s own 5-minute noise). ' : ''}
        Change it in chat, e.g. “sigorta %95 olsun”, “takılırsa hedefe in”, “sigortasız”.
      </div>
    </Section>
  )
}

const etTime = (ms: number) =>
  new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(ms)) + ' ET'

/** The buy zone: where the price is against it, what the zone does, and its window. */
function BuyZone({ w }: { w: WorkerDetail }) {
  const z = w.plan.band
  if (!z) return null
  const rt = w.status !== 'draft' ? w.runtime?.band : undefined
  const long = w.side === 'long'
  const last = rt?.last ?? null
  // A ruler from the thesis level (or a little under the zone) to the no-chase level (or a little over it).
  const pad = (z.max - z.min) * 0.6 || z.max * 0.01
  const lo = Math.min(z.invalid ?? z.min - pad, z.min - pad * 0.3, last ?? Infinity)
  const hi = Math.max(z.chase ?? z.max + pad, z.max + pad * 0.3, last ?? -Infinity)
  const at = (v: number) => `${Math.min(100, Math.max(0, ((v - lo) / (hi - lo)) * 100))}%`
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date())
  const state = !rt
    ? null
    : rt.ended
      ? `Stopped watching — ${rt.ended.text}`
      : rt.skipDay === today
        ? `No more buys today${last != null ? ` (${fmtPrice(last)})` : ''} — the next session re-arms`
        : last == null
          ? 'Waiting for a live price'
          : rt.side === 'in'
            ? z.bounceUsd != null && rt.ext && rt.ext.date === today && (long ? last < rt.ext.px + z.bounceUsd : last > rt.ext.px - z.bounceUsd)
              ? `${fmtPrice(last)} — inside the zone, waiting for the bounce (session ${long ? 'low' : 'high'} ${fmtPrice(rt.ext.px)} → ${long ? 'buys from' : 'sells from'} ${fmtPrice(long ? rt.ext.px + z.bounceUsd : rt.ext.px - z.bounceUsd)})`
              : `${fmtPrice(last)} — inside the zone${w.orders.some((o) => o.role === 'entry' && !['Cancelled', 'ApiCancelled', 'Inactive', 'Filled'].includes(o.status)) ? ', orders at IBKR' : ''}`
            : `${fmtPrice(last)} — ${rt.side === 'below' ? 'under' : 'over'} the zone, nothing ${long ? 'bought' : 'sold'} there`
  return (
    <Section title={`Buy zone · ${fmtPrice(z.min)}–${fmtPrice(z.max)}`} right={rt?.deadline && !rt.ended ? <span className="num text-[10.5px] text-fg-faint">until {etTime(rt.deadline)}</span> : null}>
      <div className="relative mb-2 mt-1 h-5" aria-hidden>
        <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-[var(--line-soft)]" />
        <div className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[var(--color-surface-3)]" style={{ left: at(z.min), width: `calc(${at(z.max)} - ${at(z.min)})` }} />
        {z.invalid != null && <div className="absolute top-1/2 h-3 w-px -translate-y-1/2 bg-down" style={{ left: at(z.invalid) }} title={`Thesis level ${fmtPrice(z.invalid)}`} />}
        {z.chase != null && <div className="absolute top-1/2 h-3 w-px -translate-y-1/2 bg-fg-faint" style={{ left: at(z.chase) }} title={`No-chase level ${fmtPrice(z.chase)}`} />}
        {last != null && !rt?.ended && <div className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[var(--color-base)] bg-fg" style={{ left: at(last) }} title={`Last ${fmtPrice(last)}`} />}
      </div>
      {state && <div className="num mb-1 text-[12px] text-fg-muted">{state}</div>}
      <ul className="space-y-0.5 text-[11.5px] leading-relaxed text-fg-subtle">
        {(w.summary.band?.lines ?? []).map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </Section>
  )
}

function Lots({ w }: { w: WorkerDetail }) {
  const live = w.runtime && w.status !== 'draft'
  // With entry adds each lot buys at its own price — show which tier buys it.
  const tiers = w.plan.lots.some((l) => (l.entry?.tier ?? 1) > 1)
  return (
    <table className="w-full text-[12px]">
      <thead>
        <tr className="text-left text-[10.5px] text-fg-faint">
          <th className="pb-1 font-medium">Lot</th>
          {tiers && <th className="pb-1 text-right font-medium">Entry</th>}
          <th className="pb-1 text-right font-medium">Shares</th>
          <th className="pb-1 text-right font-medium">Target</th>
          <th className="pb-1 text-right font-medium">Stop</th>
          {live && <th className="pb-1 text-right font-medium">Filled · open</th>}
        </tr>
      </thead>
      <tbody className="num">
        {w.plan.lots.map((pl) => {
          const rt = w.runtime?.lots.find((x) => x.lot === pl.lot)
          const target = live ? rt?.target : pl.tp?.price
          const stop = live ? rt?.stop : pl.sl.stop
          return (
            <tr key={pl.lot} className="border-t border-[var(--separator)] text-fg-muted">
              <td className="py-1 text-fg-subtle">{pl.lot}</td>
              {tiers && (
                <td className="py-1 text-right">
                  <span className="text-fg-faint">{(pl.entry?.tier ?? 1) > 1 ? `add ${pl.entry.tier - 1} ` : 'entry '}</span>
                  {pl.entry?.orderType === 'MKT' ? 'MKT' : fmtPrice(pl.entry?.ref ?? null)}
                </td>
              )}
              <td className="py-1 text-right">{pl.qty}</td>
              <td className="py-1 text-right">
                {pl.tp ? (
                  <>
                    <span className="text-fg-faint">TP{pl.tp.tier} </span>
                    {fmtPrice(target ?? null)}
                  </>
                ) : (
                  <span className="text-fg-faint">runner</span>
                )}
              </td>
              <td className="py-1 text-right">
                {pl.noStop && !(stop != null && stop > 0) ? (
                  <span className="text-fg-faint">no stop</span>
                ) : (
                  <>
                    <span className="text-fg-faint">{pl.noStop ? 'SL ' : `SL${pl.sl.tier} `}</span>
                    {fmtPrice(stop ?? null)}
                  </>
                )}
                {pl.sl.trail && <span className="text-fg-faint"> trail {pl.sl.trail.amount != null ? fmtPrice(pl.sl.trail.amount) : `${pl.sl.trail.percent}%`}</span>}
                {pl.sl.adjust && !live && pl.insurance?.trigger == null && (
                  <span className="text-fg-faint"> → {fmtPrice(pl.sl.adjust.stop)} @ {fmtPrice(pl.sl.adjust.trigger)}</span>
                )}
                <StopSteps w={w} lot={pl} />
              </td>
              {live && (
                <td className="py-1 text-right">
                  {rt?.entryFilled ?? 0}/{pl.qty} · {rt?.open ?? 0}
                  {rt && rt.closeFilled > 0 && <span className="text-fg-faint"> · {rt.closeFilled} sold</span>}
                </td>
              )}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function Orders({ w }: { w: WorkerDetail }) {
  if (w.status === 'draft' || !w.orders.length)
    return (
      <pre className="overflow-x-auto whitespace-pre rounded-lg bg-[var(--color-inset)] p-2 text-[11px] leading-relaxed text-fg-muted">
        {w.summary.ordersText.join('\n')}
      </pre>
    )
  return (
    <div className="overflow-x-auto rounded-lg bg-[var(--color-inset)] p-2">
      <table className="w-full text-[11px]">
        <tbody className="num">
          {w.orders.map((o) => (
            <tr key={o.id} className={cn('text-fg-muted', ['Cancelled', 'ApiCancelled', 'Inactive'].includes(o.status) && 'text-fg-faint line-through decoration-[var(--line-soft)]')}>
              <td className="pr-2">{o.planKey}</td>
              <td className="pr-2">
                {o.action} {o.quantity}
              </td>
              <td className="pr-2">{o.orderType}</td>
              <td className="pr-2">{o.limitPrice != null ? `lmt ${fmtPrice(o.limitPrice)}` : ''}</td>
              <td className="pr-2">{o.stopPrice != null ? `stp ${fmtPrice(o.stopPrice)}` : ''}</td>
              <td className="pr-2 no-underline">{o.status}</td>
              <td className="pr-2">{o.filled ? `${o.filled} @ ${fmtPrice(o.avgFillPrice)}` : ''}</td>
              <td className="text-fg-faint">#{o.orderId}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function WorkerBody({ w, variant }: { w: WorkerDetail; variant: 'chat' | 'page' }) {
  const broker = w.spec.broker ?? 'ibkr'
  const B = BROKER_LABEL[broker]
  const [showOrders, setShowOrders] = useState(variant === 'page')
  const [showScen, setShowScen] = useState<number | null>(null)
  const s = w.summary
  const rt = w.runtime
  const draft = w.status === 'draft'
  const fired = new Set(rt?.firedRules ?? [])
  const openShares = rt?.lots.reduce((a, x) => a + x.open, 0) ?? 0
  const failing = w.checks.filter((c) => !c.ok && c.level !== 'info')
  // Indicator values: live for a running worker, as of drafting for a draft.
  const nowOf = (id: string) => (w.marketNow ?? s.marketNow)?.find((x) => x.id === id)?.text
  const fee = s.fees
  const rv = s.revision
  return (
    <>
      {rv && (
        <div className="mx-3.5 mb-2.5 rounded-lg border border-[var(--line-soft)] px-3 py-2.5">
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-fg-faint">
            What changes at {B} · v{rv.fromVersion} → v{rv.fromVersion + 1}
          </div>
          <ul className="mt-1.5 space-y-0.5 text-[12px] text-fg-muted">
            {rv.lines.map((x, i) => (
              <li key={i} className="num">
                {x}
              </li>
            ))}
          </ul>
          {rv.warnings.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-[11.5px] text-down">
              {rv.warnings.map((x, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <TriangleAlert size={12} className="mt-[2px] shrink-0" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-1.5 text-[11px] text-fg-faint">The position stays open. Below: the worker as it runs after the change.</div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-1.5 px-3.5 pb-2.5 sm:grid-cols-4">
        {draft || !rt ? (
          <>
            {fee ? (
              // After the estimated fees of the user's IBKR plan; the gross figure underneath.
              <>
                <Metric
                  label={w.spec.noStop ? 'Max loss · no stop' : 'Max loss · after fees'}
                  value={usd(-s.maxLossNet!)}
                  tone="down"
                  sub={w.spec.noStop ? 'the whole position — your choice' : `${usd(-s.maxLoss)} + ${usd(fee.atStops)} fees`}
                />
                <Metric
                  label="Best case · after fees"
                  value={s.bestCaseNet == null ? 'Open-ended' : usd(s.bestCaseNet, true)}
                  tone={s.bestCaseNet == null ? undefined : s.bestCaseNet > 0 ? 'up' : 'down'}
                  sub={s.bestCase == null ? undefined : `${usd(s.bestCase, true)} − ${usd(fee.atTargets)} fees`}
                />
                <Metric
                  label="Reward : risk"
                  value={s.rewardRiskNet == null ? '—' : s.rewardRiskNet.toFixed(2)}
                  sub={s.rewardRisk == null ? undefined : `${s.rewardRisk.toFixed(2)} before fees`}
                />
              </>
            ) : (
              <>
                <Metric label="Max loss" value={usd(-s.maxLoss)} tone="down" />
                <Metric label="Best case" value={s.bestCase == null ? 'Open-ended' : usd(s.bestCase, true)} tone={s.bestCase == null ? undefined : 'up'} />
                <Metric label="Reward : risk" value={s.rewardRisk == null ? '—' : `${s.rewardRisk.toFixed(2)}`} />
              </>
            )}
            <Metric
              label={s.avgEntryIfAllFill != null ? 'Position · avg if all fill' : 'Position'}
              value={`${s.quantity} × ${fmtPrice(s.avgEntryIfAllFill ?? s.entryRef)}`}
            />
          </>
        ) : (
          <>
            <Metric
              label="Realized"
              value={usd(rt.realized, true)}
              tone={rt.realized > 0 ? 'up' : rt.realized < 0 ? 'down' : undefined}
              sub={rt.commissions > 0 ? `net ${usd(rt.realized - rt.commissions, true)} after ${usd(rt.commissions)} ${B} fees` : undefined}
            />
            <Metric label="Open shares" value={`${openShares} / ${s.quantity}`} />
            <Metric label="Avg entry" value={rt.avgEntry == null ? '—' : fmtPrice(rt.avgEntry)} />
            <Metric label="Planned max loss" value={usd(-(s.maxLossNet ?? s.maxLoss))} tone="down" sub={s.maxLossNet != null ? 'incl. estimated fees' : undefined} />
          </>
        )}
      </div>

      {(w.error || rt?.attention) && (
        <div className="mx-3.5 mb-2.5 flex items-start gap-2 rounded-lg border border-[var(--line-soft)] px-2.5 py-2 text-[12px] text-down">
          <TriangleAlert size={13} className="mt-[1px] shrink-0" />
          <span>{rt?.attention ?? w.error}</span>
        </div>
      )}

      <BuyZone w={w} />

      <Section title={`Lots · ${w.plan.lots.length} bracket${w.plan.lots.length === 1 ? '' : 's'}${w.plan.band ? ' (sent while the price is in the zone)' : broker === 'ibkr' ? ' at IBKR' : ' · stop at Robinhood, target watched here'}`}>
        <Lots w={w} />
      </Section>

      <Insurance w={w} />

      {s.basis && s.basis.lines.length > 0 && (
        <Section title={`Basis · ${w.symbol}’s own ${B} bars`}>
          <ul className="space-y-0.5 text-[11.5px] leading-relaxed text-fg-subtle">
            {s.basis.lines.map((x, i) => (
              <li key={i} className={cn('num', i === 0 && 'text-fg-muted')}>
                {x}
              </li>
            ))}
          </ul>
          {s.basis.warnings.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-[11.5px] text-fg-muted">
              {s.basis.warnings.map((x, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <TriangleAlert size={12} className="mt-[2px] shrink-0 text-fg-faint" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {s.conditionsText.length > 0 && (
        <Section title="Starts when">
          <ul className="space-y-0.5 text-[12px] text-fg-muted">
            {s.conditionsText.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
          {nowOf('start') && <div className="num mt-1 text-[11px] text-fg-subtle">now: {nowOf('start')}</div>}
        </Section>
      )}

      {s.rulesText.length > 0 && (
        <Section title="Automatic rules">
          <ul className="space-y-1 text-[12px] text-fg-muted">
            {s.rulesText.map((r) => (
              <li key={r.id} className="flex items-start gap-1.5">
                <span className={cn('mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full', fired.has(r.id) ? 'bg-up' : 'bg-[var(--line-soft)]')} />
                <span>
                  {r.text}
                  {fired.has(r.id) && <span className="ml-1 text-[10.5px] text-fg-faint">fired</span>}
                  {!fired.has(r.id) && nowOf(r.id) && <span className="num block text-[11px] text-fg-subtle">now: {nowOf(r.id)}</span>}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {draft && s.scenarios.length > 0 && (
        <Section
          title="What happens if…"
          right={fee ? <span className="text-[10.5px] text-fg-faint">after fees · {COMMISSION_PLAN_LABEL[fee.plan]}</span> : null}
        >
          <div className="space-y-0.5">
            {s.scenarios.map((sc, i) => (
              <button
                key={sc.label}
                type="button"
                onClick={() => setShowScen((v) => (v === i ? null : i))}
                className="block w-full rounded-md px-1 py-0.5 text-left hover:bg-[var(--color-surface-2)]"
              >
                <div className="flex items-baseline justify-between gap-3 text-[12px]">
                  <span className="min-w-0 text-fg-muted">{sc.label}</span>
                  {sc.net != null ? (
                    <span className="flex shrink-0 items-baseline gap-2">
                      <span className="num text-[10.5px] text-fg-faint">
                        {usd(sc.pnl, true)} − {usd(sc.fees)}
                      </span>
                      <span className={cn('num font-medium', sc.net > 0 ? 'text-up' : sc.net < 0 ? 'text-down' : 'text-fg-muted')}>{usd(sc.net, true)}</span>
                    </span>
                  ) : (
                    <span className={cn('num shrink-0 font-medium', sc.pnl > 0 ? 'text-up' : sc.pnl < 0 ? 'text-down' : 'text-fg-muted')}>{usd(sc.pnl, true)}</span>
                  )}
                </div>
                {sc.withoutInsurance && (
                  <div className="flex justify-between gap-3 text-[11px] text-fg-faint">
                    <span>without insurance</span>
                    <span className="num">{usd(sc.withoutInsurance.net, true)}</span>
                  </div>
                )}
                {showScen === i && <div className="mt-0.5 text-[11px] leading-relaxed text-fg-faint">{sc.detail}</div>}
              </button>
            ))}
          </div>
        </Section>
      )}

      {draft && (
        <Section title="Risk checks" right={failing.length ? <span className="text-[10.5px] text-down">{failing.length} blocking</span> : null}>
          <ul className="space-y-0.5 text-[12px]">
            {w.checks.map((c) => (
              <li key={c.id} className="flex items-start gap-1.5">
                {c.level === 'info' ? (
                  <span className="mx-[3px] mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--line)]" title="Information — does not block approval" />
                ) : c.ok ? (
                  <Check size={12} className="mt-[3px] shrink-0 text-up" />
                ) : (
                  <X size={12} className="mt-[3px] shrink-0 text-down" />
                )}
                <span className="line-clamp-2 min-w-0" title={`${c.label} — ${c.detail}`}>
                  <span className={c.ok ? 'text-fg-muted' : 'text-down'}>{c.label}</span>
                  <span className="text-fg-faint"> — {c.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {draft && s.warnings.length > 0 && (
        <Section title="Keep in mind">
          <ul className="list-disc space-y-0.5 pl-4 text-[11.5px] text-fg-subtle">
            {s.warnings.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </Section>
      )}

      <Section
        title={draft ? `Exact broker orders · ${w.spec.adopt ? w.plan.orders.filter((o) => o.role !== 'entry').length : w.plan.orders.length}` : `Broker orders · ${w.orders.length}`}
        right={
          <button type="button" onClick={() => setShowOrders((v) => !v)} className="text-[11px] text-fg-subtle hover:text-fg">
            {showOrders ? 'Hide' : 'Show'}
          </button>
        }
      >
        {showOrders && draft && w.spec.rules.some((r) => r.then.some((a) => a.do === 'moveStop')) && (
          <p className="mb-1.5 text-[11px] text-fg-subtle">{broker === 'ibkr' ? 'These go to IBKR on approval' : 'These are worked onto Robinhood after approval (each lot’s stop rests there; its target is watched by this server)'}; the rules above then move the stops as shown in the lot table.</p>
        )}
        {showOrders ? <Orders w={w} /> : null}
      </Section>

      {w.spec.note && (
        <Section title="Why (from the chat)">
          <p className="text-[11.5px] leading-relaxed text-fg-subtle">{w.spec.note}</p>
        </Section>
      )}
    </>
  )
}

/** The approval card shown in chat for a drafted worker (and its live state afterwards). */
export function WorkerCard({ id, variant = 'chat' }: { id: string; variant?: 'chat' | 'page' }) {
  const { worker: w, error, reload } = useWorker(id)
  const prefillComposer = useStore((s) => s.prefillComposer)
  const openWorker = useStore((s) => s.openWorker)
  const [armed, setArmed] = useState<null | 'approve' | 'close'>(null)
  const [busy, setBusy] = useState<null | 'approve' | 'reject' | 'close' | 'resume'>(null)
  const [quick, setQuick] = useState(false)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => {
    if (!armed) return
    const t = setTimeout(() => setArmed(null), 6_000) // a confirmation never lingers
    return () => clearTimeout(t)
  }, [armed])

  if (error && !w)
    return <div className="card mt-2 px-3.5 py-3 text-[12.5px] text-fg-subtle">Worker {id}: {error}</div>
  if (!w) return <div className="card mt-2 h-28 animate-pulse" />

  const act = async (kind: NonNullable<typeof busy>, fn: () => Promise<unknown>, ok: string) => {
    setBusy(kind)
    setMsg(null)
    try {
      await fn()
      setMsg({ tone: 'ok', text: ok })
    } catch (e) {
      setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Something went wrong' })
    } finally {
      setBusy(null)
      setArmed(null)
      void reload()
    }
  }
  const draft = w.status === 'draft'
  const live = ['waiting', 'working', 'open', 'closing', 'error'].includes(w.status)
  const changeable = ['waiting', 'working', 'open'].includes(w.status) && !w.groupId
  const blocking = w.checks.some((c) => !c.ok && c.level !== 'info')
  const paper = /^DU/i.test(w.account ?? '')
  const rv = w.summary.revision
  // "Change it in chat": the marker names the running worker; from the Workers page it opens the worker's chat.
  const editInChat = async (id: string) => {
    const st = useStore.getState()
    if (variant === 'page') {
      if (w.chatId && st.chats.some((c) => c.id === w.chatId)) await st.selectChat(w.chatId)
      else st.newChat()
    }
    prefillComposer(`Çalışan worker'ı düzenle (${id}): `)
  }
  const ended = ['done', 'cancelled', 'expired'].includes(w.status)
  const paused = /^(Paused by rule|Buy zone paused)/.test(w.runtime?.attention ?? '')
  // Re-plan: the chat gets the worker and why it ended; a new worker still needs the user's own command.
  const replan = async () => {
    const st = useStore.getState()
    if (variant === 'page') {
      if (w.chatId && st.chats.some((c) => c.id === w.chatId)) await st.selectChat(w.chatId)
      else st.newChat()
    }
    const why = w.runtime?.band?.ended?.text ?? w.error ?? w.runtime?.closeReason ?? null
    prefillComposer(`“${w.name}” worker'ını yeniden planla (${w.id})${why ? ` — ${why}` : ''}. `)
  }

  return (
    <>
    <div className={cn('card overflow-hidden', variant === 'chat' && 'mt-2')}>
      <div className="flex items-start justify-between gap-3 px-3.5 pb-2 pt-3">
        <div className="min-w-0">
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-fg-faint">
            {rv ? 'Change to a running worker' : 'Trade worker'} · {BROKER_LABEL[w.spec.broker ?? 'ibkr']}
            {paper ? ' · paper' : (w.spec.broker ?? 'ibkr') === 'robinhood' ? <span className="text-down"> · real money</span> : ''}
          </div>
          <div className="mt-0.5 truncate text-[14px] font-semibold text-fg">
            {rv ? rv.ofName : w.name}
            {!rv && (w.version ?? 1) > 1 && <span className="num ml-1.5 text-[11px] font-medium text-fg-faint">v{w.version}</span>}
          </div>
          <div className="num mt-0.5 text-[12px] text-fg-muted">{w.summary.headline}</div>
          {w.summary.sizingText && <div className="num mt-0.5 text-[11px] text-fg-faint">Size: {w.summary.sizingText}</div>}
          {draft && (w.spec.broker ?? 'ibkr') === 'robinhood' && <div className="mt-1 text-[11px] leading-snug text-fg-subtle">{BROKER_NOTE.robinhood}</div>}
        </div>
        <StatusPill status={w.status} />
      </div>

      <WorkerBody w={w} variant={variant} />

      <div className="flex flex-wrap items-center gap-1.5 border-t border-[var(--separator)] px-3.5 py-2.5">
        {draft && w.groupId && (
          <span className="text-[12px] text-fg-subtle">Part of a worker group — approve, edit or reject it on the group’s card.</span>
        )}
        {draft && rv && (
          <>
            <button
              type="button"
              disabled={!!busy || blocking}
              onClick={() =>
                armed === 'approve'
                  ? void act('approve', () => workersApi.approve(w.id, w.planHash), `Change approved — being applied at ${BROKER_LABEL[w.spec.broker ?? 'ibkr']}.`)
                  : setArmed('approve')
              }
              className={cn(btn, 'bg-[var(--color-accent)] text-[var(--color-base)]')}
            >
              <Check size={13} />
              {busy === 'approve' ? 'Applying…' : armed === 'approve' ? `Confirm — apply ${rv.lines.length} change${rv.lines.length === 1 ? '' : 's'}` : 'Approve change'}
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => void editInChat(rv.of)}
              className={cn(btn, 'border border-[var(--line-soft)] text-fg-muted hover:text-fg')}
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => void act('reject', () => workersApi.reject(w.id), 'Discarded — the worker keeps running unchanged.')}
              className={cn(btn, 'text-fg-subtle hover:text-down')}
            >
              <X size={13} /> Discard
            </button>
          </>
        )}
        {draft && !rv && !w.groupId && (
          <>
            <button
              type="button"
              disabled={!!busy || blocking}
              title={blocking ? 'A risk check is blocking this plan' : undefined}
              onClick={() =>
                armed === 'approve'
                  ? void act('approve', () => workersApi.approve(w.id, w.planHash), `Approved — orders sent to ${BROKER_LABEL[w.spec.broker ?? 'ibkr']}.`)
                  : setArmed('approve')
              }
              className={cn(btn, 'bg-[var(--color-accent)] text-[var(--color-base)]')}
            >
              <Check size={13} />
              {busy === 'approve' ? 'Placing orders…' : armed === 'approve' ? `Confirm — place ${w.spec.adopt ? w.plan.orders.filter((o) => o.role !== 'entry').length : w.plan.orders.length} orders` : 'Approve'}
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => prefillComposer(`Worker taslağını düzenle (${w.id}): `)}
              className={cn(btn, 'border border-[var(--line-soft)] text-fg-muted hover:text-fg')}
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => void act('reject', () => workersApi.reject(w.id), `Rejected — nothing was sent to ${BROKER_LABEL[w.spec.broker ?? 'ibkr']}.`)}
              className={cn(btn, 'text-fg-subtle hover:text-down')}
            >
              <X size={13} /> Reject
            </button>
            <span className="num ml-auto text-[10.5px] text-fg-faint" title="Approval is bound to exactly this plan">
              plan #{w.planHash.slice(0, 8)}
            </span>
          </>
        )}
        {live && (
          <>
            {changeable && (
              <>
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() => void editInChat(w.id)}
                  className={cn(btn, 'border border-[var(--line-soft)] text-fg-muted hover:text-fg')}
                  title="Describe the change in chat — you approve it on a change card"
                >
                  <Pencil size={13} /> Edit in chat
                </button>
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() => setQuick((v) => !v)}
                  className={cn(btn, 'border border-[var(--line-soft)] text-fg-muted hover:text-fg', quick && 'text-fg')}
                >
                  <SlidersHorizontal size={13} /> Quick edit
                </button>
              </>
            )}
            {w.runtime?.attention && (
              <button
                type="button"
                disabled={!!busy}
                onClick={() => void act('resume', () => workersApi.resume(w.id), 'Resumed.')}
                className={cn(btn, 'border border-[var(--line-soft)] text-fg-muted hover:text-fg')}
              >
                {paused ? (
                  <>
                    <Play size={13} /> Resume
                  </>
                ) : (
                  <>
                    <RotateCw size={13} /> I checked {BROKER_LABEL[w.spec.broker ?? 'ibkr']} — resume
                  </>
                )}
              </button>
            )}
            {w.status !== 'closing' && (
              <button
                type="button"
                disabled={!!busy}
                onClick={() =>
                  armed === 'close'
                    ? void act('close', () => workersApi.close(w.id), 'Stopping — cancelling its orders and closing at market.')
                    : setArmed('close')
                }
                className={cn(btn, armed === 'close' ? 'bg-down text-white' : 'border border-[var(--line-soft)] text-fg-muted hover:text-down')}
              >
                <Power size={13} />
                {armed === 'close' ? 'Confirm — cancel orders & close at market' : 'Stop worker'}
              </button>
            )}
          </>
        )}
        {ended && !w.revises && (
          <button
            type="button"
            onClick={() => void replan()}
            className={cn(btn, 'border border-[var(--line-soft)] text-fg-muted hover:text-fg')}
            title="Talk it over in chat — a new worker is created only when you say so"
          >
            <MessageSquare size={13} /> Re-plan in chat
          </button>
        )}
        {variant === 'chat' && !draft && (
          <button type="button" onClick={() => openWorker(w.id)} className={cn(btn, 'ml-auto text-fg-subtle hover:text-fg')}>
            Open in Workers <ArrowUpRight size={13} />
          </button>
        )}
        {variant === 'chat' && draft && (
          <button type="button" onClick={() => openWorker(w.id)} className="text-[11px] text-fg-faint hover:text-fg" title="Open in Workers">
            <ChevronRight size={13} />
          </button>
        )}
      </div>
      {msg && <div className={cn('px-3.5 pb-2.5 text-[12px]', msg.tone === 'ok' ? 'text-up' : 'text-down')}>{msg.text}</div>}
    </div>
    {quick && changeable && <QuickEdit w={w} onClose={() => setQuick(false)} />}
    </>
  )
}
